import React, { useEffect, useRef, useState } from 'react';
import { AgentFilledIcon } from '@domain-group/fe-co-icon';
import { notEmpty } from '../../../../utils';
import { isNoAgentPhoto } from '../../../shared/avatar';
import * as styles from './avatar-stack.style';

export const MAX_AVATARS = 3;

const hasPhoto = (url?: string | null): url is string =>
  !!url && !isNoAgentPhoto(url);

export interface StackedAgent {
  name: string;
  profileUrl: string;
  profilePhoto?: string | null;
}

const AgentAvatar = ({
  name,
  profilePhoto,
}: Omit<StackedAgent, 'profileUrl'>): JSX.Element => {
  const imageRef = useRef<HTMLImageElement>(null);
  const [failedSrc, setFailedSrc] = useState<string | null>(null);

  useEffect(() => {
    const image = imageRef.current;

    if (image && image.complete && image.naturalWidth === 0) {
      setFailedSrc(profilePhoto ?? null);
    }
  }, [profilePhoto]);

  return (
    <span
      css={styles.cell}
      role="img"
      aria-label={name}
      data-testid="avatar-stack__avatar"
    >
      {hasPhoto(profilePhoto) && profilePhoto !== failedSrc ? (
        <img
          ref={imageRef}
          css={styles.photo}
          src={profilePhoto}
          alt=""
          loading="lazy"
          onError={() => setFailedSrc(profilePhoto)}
        />
      ) : (
        <AgentFilledIcon
          viewBox="0 0 18 20"
          size="custom"
          css={styles.silhouette}
          data-testid="avatar-stack__silhouette"
        />
      )}
    </span>
  );
};

export interface AvatarStackProps {
  agents?: Array<StackedAgent | null> | null;
}

export const hasAvatars = (
  agents?: Array<StackedAgent | null> | null,
): boolean => (agents ?? []).some(notEmpty);

const AvatarStack = ({ agents }: AvatarStackProps): JSX.Element | null => {
  const shown = (agents ?? []).filter(notEmpty).slice(0, MAX_AVATARS);

  if (!shown.length) {
    return null;
  }

  return (
    <div css={styles.stack} data-testid="avatar-stack">
      {shown.map((agent) => (
        <AgentAvatar
          key={agent.profileUrl}
          name={agent.name}
          profilePhoto={agent.profilePhoto}
        />
      ))}
    </div>
  );
};

export default AvatarStack;
